import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { suppliersApi } from '../../../services/api'
import { getErrorMessage } from '../../../utils/errors'
import TableActionButton from '../../../components/TableActionButton'
import { useSortableData } from '../../../hooks/useSortableData'
import SortableTh from '../../../components/SortableTh'

export interface Supplier {
  id: number
  name: string
  contact_name: string | null
  phone: string | null
  email: string | null
  address: string | null
  ruc: string | null
  notes: string | null
  is_active: boolean
  created_at: string
}

interface SupplierForm {
  name: string
  contact_name: string
  phone: string
  email: string
  address: string
  ruc: string
  notes: string
}

const emptyForm: SupplierForm = {
  name: '',
  contact_name: '',
  phone: '',
  email: '',
  address: '',
  ruc: '',
  notes: '',
}

export default function SuppliersTab() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<SupplierForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const fetchSuppliers = async () => {
    try {
      setLoadError('')
      const res = await suppliersApi.list()
      setSuppliers(res.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching suppliers:', err)
      setLoadError('No se pudieron cargar los proveedores. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSuppliers()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (s: Supplier) => {
    setForm({
      name: s.name,
      contact_name: s.contact_name || '',
      phone: s.phone || '',
      email: s.email || '',
      address: s.address || '',
      ruc: s.ruc || '',
      notes: s.notes || '',
    })
    setEditingId(s.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        ruc: form.ruc.trim() || null,
        notes: form.notes.trim() || null,
      }
      if (editingId) {
        await suppliersApi.update(editingId, payload)
        toast.success('Proveedor actualizado')
      } else {
        await suppliersApi.create(payload)
        toast.success('Proveedor creado')
      }
      setShowForm(false)
      fetchSuppliers()
    } catch (err: any) {
      setError(getErrorMessage(err, 'Error al guardar el proveedor'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (s: Supplier) => {
    setTogglingId(s.id)
    try {
      await suppliersApi.update(s.id, { is_active: !s.is_active })
      toast.success(s.is_active ? 'Proveedor desactivado' : 'Proveedor activado')
      fetchSuppliers()
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Error al actualizar el proveedor'))
    } finally {
      setTogglingId(null)
    }
  }

  const { sortedData: sortedSuppliers, sortConfig, requestSort } = useSortableData(suppliers)

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Proveedores de café, insumos y servicios. No se pueden eliminar, solo desactivar.
        </p>
        <button
          onClick={openNew}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Proveedor
        </button>
      </div>

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && suppliers.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay proveedores registrados aún. Crea el primero para poder vincularlo a compras y cotizaciones.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Contacto" sortKey="contact_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Teléfono" sortKey="phone" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedSuppliers.map((s) => (
                  <tr key={s.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{s.name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">{s.contact_name || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">{s.phone || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">{s.email || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          s.is_active ? 'bg-green-100 text-green-800' : 'bg-cream-200 text-espresso-700'
                        }`}
                      >
                        {s.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(s)} variant="primary">
                          Editar
                        </TableActionButton>
                        <TableActionButton
                          onClick={() => handleToggleActive(s)}
                          disabled={togglingId === s.id}
                          variant="danger"
                        >
                          {togglingId === s.id ? 'Guardando...' : s.is_active ? 'Desactivar' : 'Activar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="supplier-name" className="block text-sm font-medium text-espresso-700 mb-1">
                  Nombre *
                </label>
                <input
                  id="supplier-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="supplier-contact" className="block text-sm font-medium text-espresso-700 mb-1">
                    Persona de contacto
                  </label>
                  <input
                    id="supplier-contact"
                    type="text"
                    name="contact_name"
                    value={form.contact_name}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label htmlFor="supplier-phone" className="block text-sm font-medium text-espresso-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    id="supplier-phone"
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="supplier-email" className="block text-sm font-medium text-espresso-700 mb-1">
                    Email
                  </label>
                  <input
                    id="supplier-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label htmlFor="supplier-ruc" className="block text-sm font-medium text-espresso-700 mb-1">
                    RUC
                  </label>
                  <input
                    id="supplier-ruc"
                    type="text"
                    name="ruc"
                    value={form.ruc}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="supplier-address" className="block text-sm font-medium text-espresso-700 mb-1">
                  Dirección
                </label>
                <input
                  id="supplier-address"
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label htmlFor="supplier-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="supplier-notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
